import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { postLogin } from '../../api/auth/postLogin';
import { useAuth } from '../../context/AuthContext';
import { fonts } from '@/theme/fonts';
import { Images } from '@/assets';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  const onSubmitLogin = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!email || !password) {
        setErrorMessage('이메일과 비밀번호를 모두 입력해주세요.');
        return;
      }

      setErrorMessage('');

      const data = await postLogin({
        loginId: email,
        password,
      });

      if (data.isSuccess) {
        if (data.data.role !== 'ADMIN') {
          alert('관리자 외 접근할 수 없습니다.');
          return;
        }

        login(data.data.accessToken, data.data.role);
        return;
      } else {
        if (data.error?.code === 'E_LOGIN_003') {
          alert('아이디 또는 비밀번호가 올바르지 않습니다.');
          return;
        }
      }
      alert('로그인 중 오류가 발생했습니다.');
    },
    [email, password, login]
  );

  const handleOnChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);
  };

  const handleOnChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPassword(value);
  };

  return (
    <LoginContainer>
      <Wrapper>
        <Logo src={Images.AdminLogo} alt="PingPong Admin" />
        <LoginCard>
          <LoginTitle>Admin Portal</LoginTitle>
          <Description>Sign in to access the admin dashboard</Description>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          <LoginForm onSubmit={onSubmitLogin}>
            <FormGroup>
              <label htmlFor="email">E-mail</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleOnChangeEmail}
                placeholder="Please enter the e-mail"
                autoComplete="email"
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={handleOnChangePassword}
                placeholder="Please enter the password"
                autoComplete="current-password"
              />
            </FormGroup>
            <LoginButton type="submit">Log in</LoginButton>
          </LoginForm>
        </LoginCard>
      </Wrapper>
    </LoginContainer>
  );
};

export default Login;

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: #f8fafc;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
`;

const LoginCard = styled.div`
  width: 400px;
  padding: 40px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
`;

const LoginTitle = styled.div`
  margin: 0;
  font-size: 28px;
  font-weight: ${fonts.weight.bold};
  color: #111827;
  text-align: center;
`;

const Description = styled.p`
  margin: 8px 0 28px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  label {
    font-size: 13px;
    font-weight: ${fonts.weight.medium};
    color: #374151;
  }

  input {
    height: 48px;
    padding: 0 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    transition: 0.2s;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    }
  }
`;

const ErrorMessage = styled.div`
  margin-bottom: 20px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  color: #dc2626;
  font-size: 13px;
`;

const LoginButton = styled.button`
  margin-top: 8px;
  height: 48px;
  border: none;
  border-radius: 6px;
  background: #2563eb;
  color: white;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    background: #1d4ed8;
  }

  &:active {
    transform: scale(0.99);
  }
`;

const Logo = styled.img`
  width: 180px;
  object-fit: contain;
`;
